<template>
  <v-container fluid px-0>
    <v-row>
      <v-col v-if="!readOnly" :cols="12">
        <bm-form-flow-layout
          cols="12"
          sm="6"
          md="4"
          lg="3"
          xl="2"
          row-align="center"
        >
          <bm-dropdown
            v-model="selectedNewItemValue"
            :label="$m.select + ' ' + itemNoun"
            :placeholder="$m.please_select"
            :items="items"
            :item-text="itemText"
            :item-value="itemValue"
            :item-hidden="itemHidden"
            data-qa="quantity_grid_items"
          />

          <bm-number
            v-model="selectedNumberToAdd"
            :min="allowNegative ? undefined : 0"
            enforce-min-max
            :label="$m.grid_qty"
            :suffix="getUnitByItemValue(selectedNewItemValue)"
            data-qa="quantity_grid_quantity"
          />

          <bm-number
            v-if="itemQuantityAvailable !== null"
            :value="quantityAvailable"
            :label="$m.stock_available_quantity"
            read-only
          />

          <bm-btn
            btn-class="mb-2"
            button-type="primary"
            :disabled="!canAddItem"
            data-qa="quantity_grid_add"
            @click="addItem"
            >{{ $m.add }} {{ itemNoun }}</bm-btn
          >
        </bm-form-flow-layout>
      </v-col>

      <v-col :cols="12">
        <bm-grid
          :items="value"
          :grid-configuration="mergedGridConfiguration"
          :item-noun="itemNoun"
          :item-key="rowKeyFunc()"
          :allow-drag="!readOnly && allowDragDrop"
          :allow-drop="!readOnly && allowDragDrop"
          :allow-sort="allowSort"
          @drag-drop="dragDropItem"
        >
          <template #expand="row">
            <slot name="expand" :row="row" />
          </template>
        </bm-grid>
      </v-col>
    </v-row>
  </v-container>
</template>

<script lang="ts">
import BmGrid, {
  type GridDragDropEvent,
} from "@/components/controls/grid/BmGrid.vue";
import type { VuetifyPropertyArg } from "@/components/utils/vuetifyHelpers";
import { getPropertyFromItem } from "@/components/utils/vuetifyHelpers";
import { BmGridConfiguration } from "@/components/controls/grid/BmGridData";
import GridConfigurationBuilder from "@/components/controls/grid/builder/GridConfigurationBuilder";
import DataCellNumberEdit from "@/components/controls/grid/renderers/editors/DataCellNumberEdit.vue";
import type { AddItemEvent } from "@/components/controls/quantitygrid/QuantityGridTypes";
import Guid from "@/app/types/common/guid";
import type Key from "@/app/types/common/key";
import { keys } from "@/plugins/i18n";
import { type PropType, computed, defineComponent } from "vue";
import { useGridItemsDragDrop } from "@/components/composables/dragDropUtils";
/**
 * New version of the quantity grid that allows for more customisation of the grid and the use of "real" objects as
 * values that do not need transforming.
 */
export default defineComponent({
  name: "BmQuantityGrid",
  components: { BmGrid },
  inheritAttrs: false,
  props: {
    value: {
      type: Array as PropType<any[]>,
      required: true,
    },
    valueText: {
      type: [Object, String, Function, Array] as PropType<VuetifyPropertyArg>,
      default: "text",
    },
    valueUnits: {
      type: [Object, String, Function, Array] as PropType<VuetifyPropertyArg>,
      default: () => "",
    },
    valueCount: {
      type: String,
      required: true,
    },
    valueSequence: {
      type: String,
      default: null,
    },
    valueKey: {
      type: [Object, String, Function, Array] as PropType<VuetifyPropertyArg>,
      required: false,
      default: "key",
    },
    items: {
      type: Array as PropType<readonly any[]>,
      default: () => [],
    },
    itemText: {
      type: [Object, String, Function, Array] as PropType<VuetifyPropertyArg>,
      default: "text",
    },
    itemValue: {
      type: [Object, String, Function, Array] as PropType<VuetifyPropertyArg>,
      default: "value",
    },
    itemUnits: {
      type: [Object, String, Function, Array] as PropType<VuetifyPropertyArg>,
      default: () => "",
    },
    itemNoun: {
      type: String,
      default: "",
    },
    itemHidden: {
      type: [Object, String, Function, Array] as PropType<VuetifyPropertyArg>,
      default: "isHidden",
    },
    itemQuantityAvailable: {
      type: Function as PropType<((itemValue: any) => number | null) | null>,
      default: null,
    },
    allowOrdering: {
      type: Boolean,
      default: false,
    },
    allowDragDrop: {
      type: Boolean,
      default: false,
    },
    allowNegative: {
      type: Boolean,
      default: false,
    },
    allowItemEdit: {
      type: Boolean,
      default: false,
    },
    editActionText: {
      type: Object as PropType<Key>,
      default: () => keys.edit,
    },
    defaultSelectedItemValue: {
      type: Object as PropType<any>,
      default: undefined,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    gridConfiguration: {
      type: Object as PropType<BmGridConfiguration>,
      default: () => new BmGridConfiguration(),
    },
    allowSort: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      selectedNewItemValue: null as any,
      selectedNumberToAdd: 1,
    };
  },
  computed: {
    canAddItem(): boolean {
      return this.selectedNumberToAdd && this.selectedNewItemValue;
    },
    mergedGridConfiguration(): any {
      const newConfiguration = Object.assign({}, this.gridConfiguration);
      const maker = new GridConfigurationBuilder<any>();

      if (Object.values(newConfiguration.columns).length == 0) {
        maker.addTextColumn(
          { id: "text", headingText: this.itemNoun },
          (
            row // title
          ) => getPropertyFromItem(row, this.valueText)
        );
      }

      let qtyColumn = maker
        .addNumberColumn(this.$m.keys.grid_qty, (line) =>
          getPropertyFromItem(line, this.valueCount)
        )
        .withSuffixExtractor((line) =>
          getPropertyFromItem(line, this.valueUnits)
        );
      if (!this.readOnly) {
        qtyColumn.withEditDialog(
          DataCellNumberEdit,
          (item, val) => {
            item[this.valueCount] = val;
          },
          {
            min: this.allowNegative ? undefined : 0,
          }
        );
      }

      if (!this.readOnly) {
        const actions = maker.addActionsColumn();
        if (this.allowItemEdit) {
          actions.withActionByKey(this.editActionText, (item) => {
            this.$emit("edit-item", item);
          });
        }

        if (this.allowOrdering) {
          actions.withAction(this.$m.grid_move_up, (row) =>
            this.moveItem(row, true)
          );
          actions.withAction(this.$m.grid_move_down, (row) =>
            this.moveItem(row, false)
          );
        }

        actions.withAction(this.$m.grid_remove, (item) => {
          let arrayClone = [...this.value];
          const index = arrayClone.indexOf(item);
          if (index > -1) {
            arrayClone.splice(index, 1);
            this.$emit("input", arrayClone);
            this.$emit("remove", item);
          }
        });
      }

      // concatenate the columns
      newConfiguration.columns = {
        ...newConfiguration.columns,
        ...maker.build().columns,
      };

      return newConfiguration;
    },
    quantityAvailable(): any {
      if (this.itemQuantityAvailable && this.selectedNewItemValue) {
        return this.itemQuantityAvailable(this.selectedNewItemValue);
      }
      return null;
    },
  },
  watch: {
    defaultSelectedItemValue: {
      // @ts-ignore
      handler: "defaultSelectedItemValueWatcher",
      immediate: true,
    },
  },
  methods: {
    defaultSelectedItemValueWatcher() {
      this.selectedNewItemValue = this.defaultSelectedItemValue ?? null;
    },
    getUnitByItemValue(itemId: any) {
      if (itemId) {
        const item = this.items.find((item) => {
          const itemValue = getPropertyFromItem(item, this.itemValue);
          return (
            itemValue === itemId ||
            // handle the Guid equals case
            (Guid.isGuid(itemValue.value) &&
              Guid.isGuid(itemId.value) &&
              itemValue.equals(itemId.value))
          );
        });

        return getPropertyFromItem(item, this.itemUnits);
      }

      return "";
    },
    addItem() {
      const nextSequence = this.valueSequence
        ? this.value.reduce(
            (max, value) => Math.max(max, value[this.valueSequence!]),
            -1
          ) + 1
        : 0;

      this.$emit("add-item", {
        itemValue: this.selectedNewItemValue,
        count: this.selectedNumberToAdd,
        nextSequence,
      } as AddItemEvent);
    },
    moveItem(item: any, up: boolean): void {
      const idx = this.value.indexOf(item);
      let newOrder = this.value.slice();

      if (up && idx > 0) {
        newOrder[idx] = newOrder[idx - 1];
        newOrder[idx - 1] = item;
      } else if (!up && idx < this.value.length - 1) {
        newOrder[idx] = newOrder[idx + 1];
        newOrder[idx + 1] = item;
      }

      // rewrite sequences
      if (this.valueSequence === null) {
        throw new Error("valueSequence is required if ordering is enabled");
      }
      newOrder.forEach((value, idx) => (value[this.valueSequence!] = idx));

      this.$emit("input", newOrder);
    },
    dragDropItem(event: GridDragDropEvent): void {
      if (this.valueSequence === null) {
        throw new Error("valueSequence is required if ordering is enabled");
      }

      const { onDrop } = this.useGridItemsDragDrop();
      onDrop(event);
    },
    rowKeyFunc(): (itemRow: any) => string {
      if (this.valueKey instanceof Function) {
        return this.valueKey;
      }
      return (itemRow: any) =>
        getPropertyFromItem(itemRow, this.valueKey, itemRow.key)?.toString();
    },
    useGridItemsDragDrop() {
      return {
        ...useGridItemsDragDrop(
          computed(() => this.value),
          this.rowKeyFunc(),
          (updatedValues) => {
            updatedValues.forEach(
              (value, idx) => (value[this.valueSequence!] = idx)
            );

            this.$emit("input", updatedValues);
          }
        ),
      };
    },
  },
});
</script>
